import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { translations, availableLanguages } from '../translations';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem('tekke_language');
      if (saved && (saved === 'en' || saved === 'mr' || saved === 'kn')) {
        return saved;
      }
    } catch (e) {
      // ignore localStorage errors
    }
    return 'en';
  });

  const setLanguage = (lang) => {
    if (lang === 'en' || lang === 'mr' || lang === 'kn') {
      setLanguageState(lang);
      try {
        localStorage.setItem('tekke_language', lang);
      } catch (e) {
        // ignore
      }
      document.documentElement.lang = lang;
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useMemo(() => {
    return (path, fallback = '') => {
      const keys = path.split('.');
      let current = translations[language];
      for (const key of keys) {
        if (current && current[key] !== undefined) {
          current = current[key];
        } else {
          // fallback to en
          let fbCurrent = translations.en;
          for (const fbKey of keys) {
            if (fbCurrent && fbCurrent[fbKey] !== undefined) {
              fbCurrent = fbCurrent[fbKey];
            } else {
              return fallback || path;
            }
          }
          return fbCurrent !== undefined ? fbCurrent : (fallback || path);
        }
      }
      return current !== undefined ? current : (fallback || path);
    };
  }, [language]);

  // Helper to get translated service object
  const getService = (service) => {
    if (!service) return service;
    const localized = translations[language]?.servicesList?.[service.id] || translations.en?.servicesList?.[service.id];
    if (localized) {
      return {
        ...service,
        name: localized.name || service.name,
        category: localized.category || service.category,
        description: localized.description || service.description,
      };
    }
    return service;
  };

  // Helper to get translated branch object
  const getBranch = (branch) => {
    if (!branch) return branch;
    const localized = translations[language]?.branchList?.[branch.slug] || translations.en?.branchList?.[branch.slug];
    if (localized) {
      return {
        ...branch,
        name: localized.name || branch.name,
        city: localized.city || branch.city,
        area: localized.area || branch.area,
        address: localized.address || branch.address,
        facilities: localized.facilities || branch.facilities,
      };
    }
    return branch;
  };

  // Helper to get translated doctor object
  const getDoctor = (doctor) => {
    if (!doctor) return doctor;
    const localized = translations[language]?.doctorsList?.[doctor.id] || translations.en?.doctorsList?.[doctor.id];
    if (localized) {
      return {
        ...doctor,
        name: localized.name || doctor.name,
        qualification: localized.qualification || doctor.qualification,
        specialization: localized.specialization || doctor.specialization,
        profile: localized.profile || doctor.profile,
      };
    }
    return doctor;
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        availableLanguages,
        getService,
        getBranch,
        getDoctor
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
